import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { type AdditionalLotteryReward } from '@/common/types/lottery';
import { selectCurrentInProgressLoteryData } from '@/redux/selectors/lotterySelector';
import { useAppSelector } from '@/redux/store';

import { useCurrentLotteryTier } from '../hooks';

import { AdditionalRewardItem } from './AdditionalRewardItem/AdditionalRewardItem';

interface AdditionalRewardForTicketsProps {
  className?: string;
}

const AdditionalRewardForTickets: FC<AdditionalRewardForTicketsProps> = ({
  className,
}) => {
  const { t } = useTranslation();
  const currentTicketType = useCurrentLotteryTier();
  const lotteryData = useAppSelector(selectCurrentInProgressLoteryData);
  const additionalRewards = lotteryData?.rewardsIncentive;
  const myTicketsCount = lotteryData?.myTickets?.length ?? 0;

  const generateLimitsAndRewards = ():
    | undefined
    | {
        amount: string;
        reward: AdditionalLotteryReward;
        isActive: boolean;
      }[] => {
    if (!additionalRewards) return;

    const additionalRewardsEntries = Object.entries(additionalRewards);

    return additionalRewardsEntries.map(([floorAmount, reward], index, arr) => {
      const isLast = arr.length - 1 === index;
      const floor = Number(floorAmount);

      if (isLast) {
        return {
          amount: `> ${floorAmount}`,
          reward,
          isActive: myTicketsCount >= floor,
        };
      } else {
        const nextFloorAmount = arr[index + 1][0];
        const ceilAmount = Number(nextFloorAmount) - 1;

        return {
          amount: `${floorAmount} - ${ceilAmount}`,
          reward,
          isActive: myTicketsCount >= floor && myTicketsCount <= ceilAmount,
        };
      }
    });
  };

  const list = generateLimitsAndRewards()?.reverse();

  return (
    <div className={cn('flex flex-col w-full', className)}>
      <div className="flex w-full items-center justify-between px-2 pb-[6px] border-b border-white/20">
        <span className="text-[10px] text-white/60 leading-none">
          {t('Tickets')}
        </span>
        <span className="text-[10px] text-white/60 leading-none w-[80px] text-center">
          {t('Amount')}
        </span>
        <span className="text-[10px] text-white/60 leading-none w-[60px]">
          {t('Reward')}
        </span>
      </div>
      {list?.map((props, index) => (
        <AdditionalRewardItem key={index} type={currentTicketType} {...props} />
      ))}
    </div>
  );
};

export { AdditionalRewardForTickets };
